import React from "react";
import { TransProps } from "@lingui/react";
import { getColor } from "components/utils";

export type typeInputText = {
  id: string;
  name?: string;
  step?: number;
  value?: string;
  variant?: string;
  maxValue?: number;
  minValue?: number;
  disabled?: boolean;
  required?: boolean;
  maxLength?: number;
  autoFocus?: boolean;
  placeholder?: string;
  inputClassName?: string;
  labelClassName?: string;
  asteriskPlacement?: "right";
  inputWrapClassName?: string;
  type?: "text" | "number" | "date";
  autoComplete?: string | undefined;
  label?: React.ReactElement<TransProps>;
  inputRef?: React.MutableRefObject<any>;
  errorMessage?: React.ReactElement<TransProps>;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
};

export const InputText: React.FC<typeInputText> = ({
  id,
  name,
  step,
  label,
  value,
  onBlur,
  variant,
  maxValue,
  minValue,
  onChange,
  disabled,
  inputRef,
  onKeyDown,
  autoFocus,
  maxLength,
  placeholder,
  autoComplete,
  errorMessage,
  type = "text",
  labelClassName,
  inputClassName,
  required = false,
  asteriskPlacement,
  inputWrapClassName,
}) => {
  const color = getColor(variant);

  const labelStyles = [
    "block mb-1 text-sm font-medium text-gray-700",
    labelClassName,
  ].join(" ");

  const inputStyles = [
    `appearance-none block w-full px-3 py-2 border ${
      errorMessage ? "border-red-500" : "border-gray-300"
    } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-${
      errorMessage ? "red" : color
    }-500 focus:border-${errorMessage ? "red" : color}-500 sm:text-sm`,
    inputClassName,
    disabled ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "",
  ].join(" ");

  return (
    <>
      {label ? (
        <label htmlFor={id} className={labelStyles}>
          {required && !asteriskPlacement && (
            <span className="text-red-600 mr-1">*</span>
          )}{" "}
          {label}{" "}
          {required && asteriskPlacement === "right" && (
            <span className="text-red-600 mr-1">*</span>
          )}
        </label>
      ) : null}
      <div className={inputWrapClassName}>
        <input
          id={id}
          name={name}
          type={type}
          step={step}
          value={value}
          max={maxValue}
          min={minValue}
          ref={inputRef}
          onBlur={onBlur}
          required={required}
          onChange={onChange}
          disabled={disabled}
          autoFocus={autoFocus}
          onKeyDown={onKeyDown}
          maxLength={maxLength}
          className={inputStyles}
          placeholder={placeholder}
          autoComplete={autoComplete}
        />
        {errorMessage ? (
          <p className="text-xs mt-1 text-red-500">{errorMessage}</p>
        ) : null}
      </div>
    </>
  );
};
